import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import type {
  BillingPlanBudget,
  BillingPlanBudgetItem,
  BillingPlanFragment,
  UpdateBillingPlanBudgetInput,
} from "@src/__generated__/graphql";
import type { AppStore } from "@src/stores/AppStore";
import { toApiDate } from "@src/utils/dates";
import { denominate } from "@src/utils/formatters";
import type { DateRange } from "@src/utils/types";
import { eachMonthOfInterval, isSameMonth } from "date-fns";
import { compact, uniqueId } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { ProjectBillingPlanSubRow } from "./subrow";

export class ProjectBillingPlanRow implements BillingPlanFragment {
  private appStore: AppStore;

  id;
  title;
  total_budget;
  billing_item_type;
  budget_item_type;
  budget_item_id;
  category;

  @observable
  __subRows: ProjectBillingPlanSubRow[] = [];

  constructor(
    source: BillingPlanBudget,
    period: DateRange,
    appStore: AppStore,
  ) {
    makeObservable(this);
    this.appStore = appStore;

    this.id = source.id ?? uniqueId();
    this.billing_item_type = source.billing_item_type;
    this.budget_item_type = source.budget_item_type;
    this.budget_item_id = source.budget_item_id;
    this.title = source.title;
    this.total_budget = source.total_budget;
    this.category = source.category;

    if (!source.billingPlanBudgetItem?.length) {
      this.addSubRow(undefined, period);
      return;
    }

    for (const item of source.billingPlanBudgetItem) {
      this.addSubRow(undefined, period, item);
    }
  }

  @computed
  get isDirty(): boolean {
    return this.__subRows.some((subrow) => subrow.isDirty);
  }

  @computed
  get areMonthsDirty(): boolean {
    return this.__subRows.some((subRow) => subRow.areMonthsDirty);
  }

  @computed
  get hasError(): boolean {
    return (
      this.__subRows.some((subRow) => subRow.hasError) ||
      this.duplicateSubrowsIds.size > 0
    );
  }

  @computed
  get duplicateSubrowsIds(): Set<ProjectBillingPlanSubRow["id"]> {
    const duplicateSubrowIds = new Set<ProjectBillingPlanSubRow["id"]>();

    for (const subRow of this.__subRows) {
      const duplicateSubrows = this.__subRows.filter(({ $ }) => {
        return (
          $.type.value === subRow.$.type.value &&
          $.day.value === subRow.$.day.value
        );
      });

      if (duplicateSubrows.length <= 1) continue;

      duplicateSubrowIds.add(subRow.id);
    }

    return duplicateSubrowIds;
  }

  @computed
  get planned(): number | undefined {
    if (typeof this.total_budget !== "number") return undefined;

    let planned = 0;
    for (const row of this.__subRows) {
      for (const field of row.$.months.$.values()) {
        planned += denominate(
          Number(field.$),
          this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
        );
      }
    }

    return planned;
  }

  @computed
  get difference(): number | undefined {
    if (
      typeof this.total_budget !== "number" ||
      typeof this.planned !== "number"
    )
      return undefined;
    return this.planned - this.total_budget;
  }

  @computed
  get mutationInput(): UpdateBillingPlanBudgetInput | undefined {
    return {
      category_id: this.category.id,
      budget_item_id: this.budget_item_id,
      budget_item_type: this.budget_item_type,
      billing_item_type: this.billing_item_type,
      billingPlanBudgetItem: compact(
        this.__subRows.map(({ mutationInput }) => mutationInput),
      ),
    };
  }

  @computed
  get isRemoveButtonDisabled() {
    const firstSubRow = this.__subRows?.[0];
    return (
      this.__subRows.length === 1 &&
      firstSubRow.areMonthsEmpty &&
      !firstSubRow.$.type.$ &&
      !firstSubRow.$.day.$
    );
  }

  @action.bound
  addSubRow(
    insertAfterId: ProjectBillingPlanSubRow["id"] | undefined,
    period: DateRange,
    data?: BillingPlanBudgetItem,
  ) {
    const interval = eachMonthOfInterval(period);
    const monthCount = interval.length;

    const newSubRow = new ProjectBillingPlanSubRow(
      this.appStore,
      uniqueId(),
      data
        ? data.months.length === monthCount
          ? data
          : {
              months: interval.map((month) => {
                const monthData = data.months.find((item) =>
                  isSameMonth(item.date, month),
                );

                return {
                  date: toApiDate(month),
                  value: monthData?.value ?? 0,
                  billingPlanInvoice: monthData?.billingPlanInvoice ?? [],
                };
              }),
              day: data.day,
              invoice_type: data.invoice_type,
            }
        : {
            months: interval.map((month) => ({
              date: toApiDate(month),
              value: 0,
              billingPlanInvoice: [],
            })),
          },
    );

    if (!insertAfterId) {
      this.__subRows.push(newSubRow);
      return;
    }

    if (this.id === insertAfterId) {
      this.__subRows.splice(0, 0, newSubRow);
      return;
    }

    let index: number | undefined;
    const subRowsCount = this.__subRows.length;

    for (let i = 0; i < subRowsCount; i++) {
      if (this.__subRows[i].id !== insertAfterId) continue;
      index = i;
      break;
    }

    if (typeof index !== "number") {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong with finding index, please try again.`,
      });
      captureEvent({
        message: "FE: Couldn't find index of matching row.",
      });
      return;
    }

    const previousSubRow = this.__subRows.find(
      (subRow) => subRow.id === insertAfterId,
    );
    newSubRow.$.type.reset(previousSubRow?.$.type.$);
    newSubRow.$.day.reset(previousSubRow?.$.day.$);

    this.__subRows.splice(index + 1, 0, newSubRow);
  }

  @action.bound
  removeRow(subRowId: string, period: DateRange) {
    if (this.__subRows.length > 1) {
      this.__subRows = this.__subRows.filter(({ id }) => id !== subRowId);
    } else {
      this.__subRows.find(({ id }) => id === subRowId)?.clearRow(period);
    }
  }
}
